import type { SportEvent } from '../../types/SportEventTypes';
import { participantMainId, readPayload } from '../shared';

/**
 * Participant fields that make a player "featured" in a given NBA event type.
 * Basketball events name a single protagonist per event: baskets / free throws
 * carry a `shooter`; rebounds / fouls / turnovers / violations / substitutions
 * carry a `player`. Ids are read via the canonical `id.main` join key — the
 * same one the autoIso registration keys players on — so the clip drawer's
 * "Featured in Event" shortcuts resolve for NBA the way they already do for NHL.
 *
 * Team-attributed plays (team rebounds, deadball rebounds, shot-clock
 * turnovers) carry no participant; `participantMainId` returns null for those
 * and they're dropped — there's no individual to feature.
 */
const FEATURED_FIELDS_BY_TYPE: Record<string, ReadonlyArray<string>> = {
  basket_made_2pt: ['shooter'],
  basket_made_3pt: ['shooter'],
  basket_missed_2pt: ['shooter'],
  basket_missed_3pt: ['shooter'],
  free_throw_made: ['shooter'],
  free_throw_missed: ['shooter'],
  rebound_offensive: ['player'],
  rebound_defensive: ['player'],
  foul_personal: ['player'],
  foul_offensive: ['player'],
  foul_drawn: ['player'],
  turnover: ['player'],
  violation: ['player'],
  substitution: ['player'],
};

export function nbaFeaturedParticipantIds(event: SportEvent): string[] {
  const { type, data } = readPayload(event);
  if (!type || !data) return [];
  const fields = FEATURED_FIELDS_BY_TYPE[type];
  if (!fields) return [];
  return fields
    .map((field) => participantMainId(data[field]))
    .filter((id): id is string => id != null);
}
